import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bar, Doughnut, Line, Radar } from 'react-chartjs-2';
import 'chart.js/auto';
import { useAuth } from '@clerk/clerk-react';
import {
  ArrowUpRight,
  Award,
  BookCopy,
  BookOpenCheck,
  BrainCircuit,
  ChartNoAxesCombined,
  CheckCircle2,
  FileCheck2,
  RefreshCw,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import './Dashboard.css';

const clampPercent = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.min(100, number)) : 0;
};

const displayPercent = (value) => `${Math.round(clampPercent(value))}%`;

const Dashboard = () => {
  const { getToken } = useAuth();
  const url = process.env.REACT_APP_BACK_END_URL;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDashboardData = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      const token = await getToken();
      const response = await fetch(`${url}/api/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      if (!response.ok) throw new Error('Unable to load your performance data.');
      const responseData = await response.json();
      setData(responseData);
    } catch (requestError) {
      if (requestError.name !== 'AbortError') {
        console.error('Error loading performance dashboard:', requestError);
        setError('Your performance data could not be loaded. Please try again.');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [getToken, url]);

  useEffect(() => {
    const controller = new AbortController();
    fetchDashboardData(controller.signal);
    return () => controller.abort();
  }, [fetchDashboardData]);

  const metrics = useMemo(() => {
    if (!data) return [];
    const caseCount = Number(data.caseAttemptCount ?? data.lineChartData?.data?.length) || 0;
    const mathCount = Number(data.totalQuestions) || 0;
    const courseStarted = Array.isArray(data.lessonsCompleted)
      ? data.lessonsCompleted.some(Boolean)
      : clampPercent(data.percentComplete) > 0;
    const resumeCount = Number(data.resumeGradeCount) || 0;
    return [
      {
        key: 'cases',
        label: 'Case performance',
        value: clampPercent(data.averageCaseStudyGrade),
        available: caseCount > 0,
        icon: BookCopy,
        status: caseCount > 0
          ? (clampPercent(data.averageCaseStudyGrade) >= 83 ? 'Strong performance' : 'Keep practicing')
          : 'Complete your first graded case',
        action: 'Practice a case',
        to: '/cases',
      },
      {
        key: 'math',
        label: 'Math accuracy',
        value: clampPercent(data.avgMath),
        available: mathCount > 0,
        icon: BrainCircuit,
        status: mathCount > 0
          ? `${Number(data.correctQuestions) || 0} of ${mathCount} correct`
          : 'Start a math practice set',
        action: 'Practice math',
        to: '/math',
      },
      {
        key: 'course',
        label: 'Course progress',
        value: clampPercent(data.percentComplete),
        available: courseStarted,
        icon: BookOpenCheck,
        status: courseStarted
          ? (clampPercent(data.percentComplete) >= 83 ? 'Nearly complete' : 'Continue learning')
          : 'Begin the foundations course',
        action: 'Continue course',
        to: '/intro',
      },
      {
        key: 'resume',
        label: 'Resume score',
        value: clampPercent(data.averageResumeGrade),
        available: resumeCount > 0,
        icon: FileCheck2,
        status: resumeCount > 0
          ? (clampPercent(data.averageResumeGrade) >= 83 ? 'Application ready' : 'Review your resume')
          : 'Run your first resume review',
        action: 'Review resume',
        to: '/resumeReview',
      },
    ];
  }, [data]);

  const readiness = metrics.length
    ? Math.round(metrics.reduce((sum, metric) => sum + metric.value, 0) / metrics.length)
    : 0;
  const nextFocus = metrics.length
    ? metrics.reduce((lowest, metric) => (metric.value < lowest.value ? metric : lowest), metrics[0])
    : null;

  if (loading) {
    return (
      <div className="performance-dashboard">
        <Navbar />
        <main className="performance-shell performance-state is-loading" aria-live="polite">
          <div className="performance-skeleton is-hero" />
          <div className="performance-skeleton-grid">
            {[0, 1, 2, 3].map((item) => <div className="performance-skeleton" key={item} />)}
          </div>
          <div className="performance-skeleton is-chart" />
          <span className="sr-only">Loading performance dashboard</span>
        </main>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="performance-dashboard">
        <Navbar />
        <main className="performance-shell performance-state is-error">
          <span><Sparkles size={16} /> Performance workspace</span>
          <h1>We couldn’t load your progress.</h1>
          <p>{error}</p>
          <button type="button" onClick={() => fetchDashboardData()}><RefreshCw size={17} /> Try again</button>
        </main>
      </div>
    );
  }

  const lineLabels = data.lineChartData?.labels || [];
  const lineValues = (data.lineChartData?.data || []).map(clampPercent);
  const topicLabels = data.barGraphData?.labels || [];
  const topicValues = (data.barGraphData?.data || []).map(clampPercent);
  const attempts = Number(data.caseAttemptCount ?? lineValues.length) || 0;
  const mathScore = clampPercent(data.avgMath);
  const totalQuestions = Number(data.totalQuestions) || 0;
  const correctQuestions = Number(data.correctQuestions) || 0;

  const radarData = {
    labels: ['Cases', 'Math', 'Resume', 'Course'],
    datasets: [{
      data: metrics.map((metric) => metric.value),
      backgroundColor: 'rgba(112, 75, 232, 0.16)',
      borderColor: '#704be8',
      borderWidth: 2,
      pointBackgroundColor: '#ddff58',
      pointBorderColor: '#25183c',
      pointBorderWidth: 1,
      pointRadius: 4,
    }],
  };

  const lineData = {
    labels: lineLabels,
    datasets: [{
      data: lineValues,
      borderColor: '#704be8',
      backgroundColor: 'rgba(112, 75, 232, 0.1)',
      borderWidth: 3,
      pointBackgroundColor: '#ddff58',
      pointBorderColor: '#25183c',
      pointBorderWidth: 1,
      pointRadius: 4,
      pointHoverRadius: 6,
      tension: 0.3,
      fill: true,
    }],
  };

  const topicData = {
    labels: topicLabels,
    datasets: [{
      data: topicValues,
      backgroundColor: topicValues.map((value) => value === Math.max(...topicValues) ? '#ddff58' : '#704be8'),
      borderColor: '#25183c',
      borderWidth: 1,
      borderRadius: 0,
      barThickness: 22,
    }],
  };

  const mathData = {
    labels: totalQuestions ? ['Correct', 'Incorrect'] : ['No attempts'],
    datasets: [{
      data: totalQuestions ? [correctQuestions, Math.max(totalQuestions - correctQuestions, 0)] : [1],
      backgroundColor: totalQuestions ? ['#704be8', '#e7e1f8'] : ['#e7e1f8'],
      borderColor: '#25183c',
      borderWidth: 1,
    }],
  };

  const sharedScale = {
    min: 0,
    max: 100,
    ticks: {
      color: '#6e6879',
      font: { family: 'DM Sans', size: 11 },
      callback: (value) => `${value}%`,
    },
    grid: { color: 'rgba(37, 24, 60, 0.1)' },
    border: { color: 'rgba(37, 24, 60, 0.22)' },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? false
      : { duration: 650 },
    interaction: { intersect: false, mode: 'index' },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (context) => `${Math.round(context.parsed.y)}%` } },
    },
    scales: {
      y: sharedScale,
      x: {
        ticks: { color: '#6e6879', font: { family: 'DM Sans', size: 11 } },
        grid: { display: false },
        border: { color: 'rgba(37, 24, 60, 0.22)' },
      },
    },
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? false
      : { duration: 650 },
    plugins: { legend: { display: false } },
    scales: {
      r: {
        min: 0,
        max: 100,
        ticks: { display: false, stepSize: 20 },
        grid: { color: 'rgba(37, 24, 60, 0.13)' },
        angleLines: { color: 'rgba(37, 24, 60, 0.13)' },
        pointLabels: { color: '#25183c', font: { family: 'DM Sans', size: 11, weight: 600 } },
      },
    },
  };

  const topicOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    animation: typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? false
      : { duration: 650 },
    plugins: { legend: { display: false } },
    scales: {
      x: sharedScale,
      y: {
        ticks: { color: '#25183c', font: { family: 'DM Sans', size: 11, weight: 600 } },
        grid: { display: false },
        border: { display: false },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? false
      : { duration: 650 },
    cutout: '76%',
    plugins: { legend: { display: false }, tooltip: { enabled: Boolean(totalQuestions) } },
  };

  const forecasts = [data.regressionDetails?.y, data.mlGradePrediction]
    .filter((value) => value != null && Number.isFinite(Number(value)))
    .map(clampPercent);
  const forecast = attempts >= 2 && forecasts.length
    ? Math.round(forecasts.reduce((sum, value) => sum + value, 0) / forecasts.length)
    : null;

  const hasHighlights = attempts > 0
    && data.bestGrade != null
    && data.worstGrade != null
    && Number.isFinite(Number(data.bestGrade))
    && Number.isFinite(Number(data.worstGrade));

  return (
    <div className="performance-dashboard">
      <Navbar />

      <header className="performance-hero">
        <div className="performance-shell performance-hero-grid">
          <div className="performance-hero-copy">
            <span className="performance-kicker"><Sparkles size={16} /> Performance workspace</span>
            <h1>Turn practice into a clearer next move.</h1>
            <p>See where your preparation is building momentum, understand the gaps, and choose what to practice next.</p>
          </div>
          <section className="performance-readiness" aria-label={`Overall readiness ${readiness} percent`}>
            <div className="performance-readiness-top"><span>Overall readiness</span><ChartNoAxesCombined size={22} /></div>
            <strong>{readiness}<small>%</small></strong>
            <div className="performance-readiness-foot">
              <span>{attempts} case {attempts === 1 ? 'attempt' : 'attempts'}</span>
              <span>4 preparation areas</span>
            </div>
          </section>
        </div>
      </header>

      <main className="performance-shell performance-main">
        <section className="performance-metrics" aria-label="Performance overview">
          {metrics.map((metric, index) => {
            const Icon = metric.icon;
            return (
              <article className="performance-metric" key={metric.key} style={{ '--metric-index': index }}>
                <div className="performance-metric-head"><span>{String(index + 1).padStart(2, '0')}</span><Icon size={20} /></div>
                <div className="performance-metric-body">
                  <small>{metric.label}</small>
                  <strong>{metric.available ? displayPercent(metric.value) : '—'}</strong>
                  <p>{metric.status}</p>
                  <div className="performance-meter" aria-hidden="true"><span style={{ width: `${metric.available ? metric.value : 0}%` }} /></div>
                </div>
                <Link to={metric.to}>{metric.action} <ArrowUpRight size={17} /></Link>
              </article>
            );
          })}
        </section>

        {nextFocus && (
          <section className="performance-focus">
            <div>
              <span><Target size={17} /> Recommended next focus</span>
              <h2>Build momentum in {nextFocus.label.toLowerCase()}.</h2>
            </div>
            <p>{nextFocus.available
              ? `Your current score is ${displayPercent(nextFocus.value)}. One focused practice block here will make the biggest difference to your overall readiness.`
              : 'This area has not been measured yet. Start here to build a more complete view of your preparation readiness.'}</p>
            <Link to={nextFocus.to}>{nextFocus.action} <ArrowUpRight size={18} /></Link>
          </section>
        )}

        <section className="performance-section-heading">
          <div><span>Progress over time</span><h2>Read the pattern, then act on it.</h2></div>
          <p>These views combine your case, math, course, and application activity into one preparation picture.</p>
        </section>

        <div className="performance-chart-grid is-primary">
          <section className="performance-chart-card is-trend">
            <header><div><span>Case performance</span><h3>Recent case scores</h3></div><TrendingUp size={22} /></header>
            <p className="performance-chart-description">Your latest saved score for each graded case, ordered chronologically.</p>
            {lineValues.length ? (
              <div className="performance-chart-canvas is-line" role="img" aria-label="Line chart of recent case scores"><Line data={lineData} options={lineOptions} /></div>
            ) : (
              <div className="performance-chart-empty"><BookCopy size={24} /><h4>No case trend yet.</h4><p>Complete your first graded case to begin the timeline.</p><Link to="/cases">Choose a case</Link></div>
            )}
          </section>

          <section className="performance-chart-card is-radar">
            <header><div><span>Preparation mix</span><h3>Readiness profile</h3></div><Award size={22} /></header>
            <p className="performance-chart-description">A balanced view across four core preparation areas.</p>
            <div className="performance-chart-canvas is-radar-canvas" role="img" aria-label="Radar chart of readiness across cases, math, resume, and course progress"><Radar data={radarData} options={radarOptions} /></div>
          </section>
        </div>

        <div className="performance-chart-grid is-secondary">
          <section className="performance-chart-card is-topics">
            <header><div><span>Skill map</span><h3>Case performance by topic</h3></div><Trophy size={22} /></header>
            <p className="performance-chart-description">Compare your average score across the case areas you have practiced.</p>
            {topicValues.some((value) => value > 0) ? (
              <div className="performance-chart-canvas is-bar" role="img" aria-label="Bar chart of average case score by topic"><Bar data={topicData} options={topicOptions} /></div>
            ) : (
              <div className="performance-chart-empty is-compact"><Trophy size={22} /><h4>Your topic map will appear here.</h4><p>Submit graded cases across different frameworks to compare performance.</p></div>
            )}
          </section>

          <div className="performance-insight-stack">
            <section className="performance-chart-card is-math">
              <header><div><span>Accuracy</span><h3>Math practice</h3></div><BrainCircuit size={22} /></header>
              <div className="performance-math-layout">
                <div className="performance-chart-canvas is-donut" role="img" aria-label={`${correctQuestions} correct answers out of ${totalQuestions} math questions`}>
                  <Doughnut data={mathData} options={doughnutOptions} />
                  <div className="performance-donut-label"><strong>{displayPercent(mathScore)}</strong><span>{totalQuestions} attempted</span></div>
                </div>
                <div className="performance-math-copy">
                  <p><strong>{correctQuestions}</strong> correct answers</p>
                  <p><strong>{Math.max(totalQuestions - correctQuestions, 0)}</strong> to review</p>
                  <Link to="/math">Practice math <ArrowUpRight size={16} /></Link>
                </div>
              </div>
            </section>

            <section className="performance-highlights">
              <span>Case highlights</span>
              {hasHighlights ? (
                <div className="performance-highlight-grid">
                  <div><CheckCircle2 size={20} /><small>Strongest area</small><strong>{displayPercent(data.bestGrade)}</strong><p>{data.bestGradeTopic || 'Case practice'}</p></div>
                  <div><Target size={20} /><small>Next opportunity</small><strong>{displayPercent(data.worstGrade)}</strong><p>{data.worstGradeTopic || 'Case practice'}</p></div>
                </div>
              ) : (
                <div className="performance-highlight-empty"><p>Complete a graded case to reveal your strongest area and next opportunity.</p></div>
              )}
            </section>
          </div>
        </div>

        <section className="performance-forecast">
          <div><span><Sparkles size={16} /> Performance forecast</span><h2>{forecast == null ? 'Your forecast is waiting for more practice.' : `Your next case is tracking toward ${forecast}%.`}</h2></div>
          <p>{forecast == null ? 'Complete at least two graded cases to unlock a useful trend forecast.' : 'This is an estimate based on your prior case performance—not a guarantee. Use it as a signal for where your preparation is heading.'}</p>
          <Link to="/cases">Keep practicing <ArrowUpRight size={18} /></Link>
        </section>
      </main>
    </div>
  );
};

export default Dashboard;
